import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { showCombinedErrorToast } from "~/components/shared/error-toast";
import CustomFormWrapper from "~/components/shared/form-wrapper";
import { Button, Stepper } from "~/components/ui";

import { CreatePTWSchema, TCreatePTW } from "./types";
import DetailsStep from "./steps/details";
import PrerequisitesStep from "./steps/prerequisites";
import HazardsGasStep from "./steps/hazards";
import SummaryStep from "./steps/summary";
import { useCreatePTWMutation } from "~/redux/api/dailyOps";

const PTWForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [createPTW] = useCreatePTWMutation();

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TCreatePTW>({
    resolver: zodResolver(CreatePTWSchema),
    mode: "onSubmit",
    defaultValues: {
      title: "",
      location: "",
      permitType: "",
      description: "",
      validFrom: new Date(),
      validTo: new Date(),
    },
  });

  const onSubmit = async (data: TCreatePTW) => {
    try {
      const payload = {
        title: data.title,
        description: data.description,
        location: data.location,
        permitType: data.permitType,
        jsaId: data.jsaId?.value,
        siteId: data.siteId?.value,
        validFrom: data.validFrom,
        validTo: data.validTo,
      };
      await createPTW({ body: payload }).unwrap();
      toast.success("Permit To Work created successfully");
      navigate("/daily-ops/ptw");
    } catch (err) {
      toast.error("Failed to create Permit To Work");
    }
  };

  const STEPS = useMemo(
    () => [
      {
        title: "Details",
        description: "Permit details",
        content: (
          <DetailsStep control={control} errors={errors} register={register} />
        ),
      },
      {
        title: "Prerequisites",
        description: "JSA Prerequisites",
        content: <PrerequisitesStep />,
      },
      {
        title: "Hazards & Gas",
        description: "Hazards and gas readings",
        content: <HazardsGasStep />,
      },
      {
        title: "Summary",
        description: "Review and submit",
        content: <SummaryStep watch={watch} />,
      },
    ],
    [control, errors, register, watch],
  );

  const goToNextStep = useCallback(() => {
    setCurrentStep((prev) => (prev === STEPS.length - 1 ? prev : prev + 1));
  }, [STEPS.length]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prev) => (prev <= 0 ? prev : prev - 1));
  }, []);

  const [isSubmiting, setIsSubmiting] = useState(false);

  useEffect(() => {
    if (isSubmiting && Object.values(errors).length > 0) {
      showCombinedErrorToast(errors);
      setIsSubmiting(false);
    }
    return () => {
      setIsSubmiting(false);
    };
  }, [isSubmiting, errors]);

  return (
    <div className="relative w-full">
      <form id="create-ptw" onSubmit={handleSubmit(onSubmit)}>
        <CustomFormWrapper className="">
          <div className="mx-auto w-full rounded-xl border border-neutral-200 bg-white px-10 py-8 mb-6">
            <Stepper
              currentStep={currentStep}
              steps={STEPS}
              setCurrentStep={setCurrentStep}
            />
            <div className="mt-8">{STEPS[currentStep].content}</div>
          </div>

          {/* Footer Controls */}
          <div className="flex w-full items-center justify-between rounded-xl border border-neutral-200 bg-white p-6">
            <Button
              type="button"
              variant="outline"
              onClick={goToPreviousStep}
              disabled={currentStep === 0}
            >
              Previous
            </Button>
            {currentStep < STEPS.length - 1 ? (
              <Button type="button" onClick={goToNextStep} variant="default">
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                variant="default"
                onClick={() => setIsSubmiting(true)}
              >
                Submit
              </Button>
            )}
          </div>
        </CustomFormWrapper>
      </form>
    </div>
  );
};

export default PTWForm;
